import { createRouter, createWebHistory } from "vue-router";
import { useSessionStore } from "@/stores/session";
import LandingPage from "../components/LandingPage.vue";
import LoginPage from "../components/LoginPage.vue";
import SignUpPage from "@/components/SignUpPage.vue";
import ContactPage from "../components/ContactPage.vue";
import HomePage from "../components/HomePage.vue";
import HealthPage from "../components/HealthPage.vue";

// NOTE: When you add a route here, also add its path to the SPA-fallback
// alternation in `public/serve.json`. The prod image serves the built bundle
// with `serve` and no `--single` flag (dropped so the standalone static
// /about page is reachable; --single would shadow it). serve.json enumerates
// the SPA routes that fall back to index.html, so a route missing from it
// will 404 on a hard refresh / direct navigation.
const routes = [
  { path: "/", component: LandingPage },
  { path: "/health", component: HealthPage },
  { path: "/login", component: LoginPage },
  { path: "/signup", component: SignUpPage },
  { path: "/contact", component: ContactPage },
  // Add protected routes here
  {
    path: "/chat",
    component: HomePage,
    meta: { requiresAuth: true, role: "user", lockScroll: true },
  },
  {
    path: "/configuration",
    component: HomePage,
    meta: { requiresAuth: true, role: "admin" },
  },
  {
    path: "/data",
    component: HomePage,
    meta: { requiresAuth: true, role: "admin" },
  },
  { path: "/manage", redirect: "/users" },
  {
    path: "/mentees",
    component: HomePage,
    meta: { requiresAuth: true, role: "mentor", lockScroll: true },
  },
  {
    path: "/menteesuggestion",
    component: HomePage,
    meta: { requiresAuth: true, role: "mentor", lockScroll: true },
  },
  {
    path: "/monitor",
    component: HomePage,
    meta: { requiresAuth: true, role: "admin" },
  },
  {
    path: "/settings",
    component: HomePage,
    meta: { requiresAuth: true, role: "user", lockScroll: true },
  },
  {
    path: "/users",
    component: HomePage,
    meta: { requiresAuth: true, role: "admin", lockScroll: true },
  },
  {
    path: "/verify-phone",
    name: "PhoneVerification",
    component: () => import("@/components/PhoneVerificationPage.vue"),
    meta: { requiresAuth: true },
  },
  {
    path: "/home",
    redirect: () => {
      const session = useSessionStore();
      if (!session.isAuthenticated) {
        return "/login";
      } else if (session.rolesIncludes("admin")) {
        return "/users";
      } else if (session.rolesIncludes("mentor")) {
        return "/mentees";
      } else if (
        session.rolesIncludes("mentee") ||
        session.rolesIncludes("user")
      ) {
        return "/chat";
      } else {
        return "/";
      }
    },
  },
  {
    path: "/reset-password",
    name: "PasswordReset",
    component: () => import("@/components/PasswordResetPage.vue"),
    meta: { requiresAuth: false }, // Unauthenticated route
  },
];

const router = createRouter({
  history: createWebHistory(),
  routes,
});

router.beforeEach(async (to, from, next) => {
  const session = useSessionStore();

  if (to.matched.some((r) => r.meta.requiresAuth)) {
    const hasToken = !!sessionStorage.getItem("authToken");
    if (!hasToken) return next({ path: "/login" });
    // if (!session.isAuthenticated) return next({ path: '/login' });

    // Only gate on phone verification if we can safely decide
    try {
      if (to.name !== "PhoneVerification") {
        if (session.phoneVerified === null) {
          const ok = await session.checkVerificationStatus();
          if (ok === false) {
            return next({
              name: "PhoneVerification",
              query: { redirect: to.fullPath },
            });
          }
        } else if (session.phoneVerified === false) {
          return next({
            name: "PhoneVerification",
            query: { redirect: to.fullPath },
          });
        }
      }
    } catch (e) {
      // Don’t abort navigation on a transient error
      console.warn("verify check failed once; letting navigation proceed", e);
    }

    // role gate
    if (
      to.matched.some((r) => r.meta.role && !session.rolesIncludes(r.meta.role))
    ) {
      return next({ path: "/home" });
    }
  }
  next();
});

router.afterEach((to) => {
  document.title = "Olivia Leadership Coach";
  const shouldLock = to.matched.some((r) => r.meta.lockScroll);
  document.documentElement.classList.toggle("lock-scroll", shouldLock);
  document.body.classList.toggle("lock-scroll", shouldLock);
});

export default router;
